// Core Libraries
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { isString } from 'lodash'
import { AsyncSearchSelect, Button, FormField, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch } from '@ideon-api/ideon-ui'
import { toInputProps, toOptionProps, toSelectProps, toToggleProps } from './formAdapters'

// PD-30386: thin ideon-ui field adapters for EditForm's `Form`, built on
// formAdapters -- the label/FormField/error markup lives here; the
// (name, value, handleChange) <-> control-event translation lives in
// formAdapters and is not repeated. Shared across screens as EditForm
// children -- no issuer-status specifics.

export function IdeonInput({
  name, label, value, handleChange, errorMessage, type, placeholder, min, max, className
}) {
  return (
    <FormField label={label} htmlFor={name} error={errorMessage} className={className}>
      <Input
        id={name}
        name={name}
        type={type || 'text'}
        placeholder={placeholder}
        min={min}
        max={max}
        hasError={!!errorMessage}
        {...toInputProps({ name, value, handleChange })}
      />
    </FormField>
  )
}

IdeonInput.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number
}

// Options are either plain strings or howie's `{ value, text }` config shape
// (State.all_display_name, OptionsListable) -- the same normalization
// SelectField.js uses. `cast` (see formAdapters#toSelectProps) round-trips a
// non-string-backed field, e.g. `cast={Number}` for state_id.
export function IdeonSelect({
  name, label, value, handleChange, errorMessage, options, cast, className
}) {
  const normalizedOptions = options.map((option) => (
    isString(option) ? { value: option, label: option } : { value: option.value, label: option.text }
  ))

  return (
    <FormField label={label} htmlFor={name} error={errorMessage} className={className}>
      <Select {...toSelectProps({ name, value, handleChange }, { cast })}>
        <SelectTrigger id={name}>
          <SelectValue placeholder={`Select ${label}`} />
        </SelectTrigger>
        <SelectContent>
          {normalizedOptions.map((option) => (
            <SelectItem key={option.value} value={String(option.value)}>{option.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  )
}

IdeonSelect.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.arrayOf(PropTypes.string)
  ]).isRequired,
  cast: PropTypes.func
}

// A remote-searched field. `attributes` (the whole loaded record, which
// `Form` hands every child) carries the nested association -- e.g. the
// issuer status's own `issuer`/`private_exchange` -- so the current
// selection's label is available on first render without a search;
// `recordLabel` builds it (and every option `onSearch` returns) from that
// same record shape.
export function IdeonAsyncField({
  name, label, attributes, handleChange, errorMessage, onSearch, associationKey, recordLabel, placeholder,
  className
}) {
  const record = attributes[associationKey]
  const [selectedOption, setSelectedOption] = useState(
    record ? { value: String(record.id), label: recordLabel(record) } : null
  )
  const { onChange } = toOptionProps({ name, handleChange })

  return (
    <FormField label={label} htmlFor={name} error={errorMessage} className={className}>
      <AsyncSearchSelect
        id={name}
        value={selectedOption}
        onChange={(option) => {
          setSelectedOption(option)
          onChange(option)
        }}
        onSearch={onSearch}
        placeholder={placeholder}
      />
    </FormField>
  )
}

IdeonAsyncField.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  attributes: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  onSearch: PropTypes.func.isRequired,
  associationKey: PropTypes.string.isRequired,
  recordLabel: PropTypes.func.isRequired,
  placeholder: PropTypes.string
}

// The form's own submit/cancel pair. Save is a plain `type="submit"`, so it
// triggers the enclosing <form>'s onSubmit -- i.e. EditForm's existing submit
// path -- rather than reaching for the handler itself. Form still renders its
// legacy SubmitButton as a direct child of the form; the screen's CSS hides
// that one, and this nested pair is what the operator sees.
export function IdeonFormActions({ submitLabel, cancelLabel, onCancel, loading, className }) {
  return (
    <div className={`ideon-form-actions flex items-center gap-3 ${className || ''}`}>
      <Button type="submit" variant="filled" disabled={loading}>
        {loading ? 'Saving\u2026' : submitLabel}
      </Button>
      <Button type="button" variant="outline" onClick={onCancel}>
        {cancelLabel || 'Cancel'}
      </Button>
    </div>
  )
}

IdeonFormActions.propTypes = {
  className: PropTypes.string,
  submitLabel: PropTypes.string.isRequired,
  cancelLabel: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool
}

// A boolean rendered as an ideon-ui Switch. Switch and Checkbox share the same
// Radix contract, so this goes through toToggleProps too.
export function IdeonSwitch({
  name, label, description, value, handleChange, disabled, title, className
}) {
  return (
    <div className={`field ${className || ''}`} title={title}>
      <div className="flex items-center gap-3">
        <Switch
          id={name}
          name={name}
          disabled={disabled}
          {...toToggleProps({ name, value, handleChange })}
        />
        <Label htmlFor={name}>{label}</Label>
      </div>
      {description && <p className="mt-1 text-sm">{description}</p>}
    </div>
  )
}

IdeonSwitch.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.bool,
  handleChange: PropTypes.func,
  disabled: PropTypes.bool,
  title: PropTypes.string
}
